import React, { useEffect, useState } from "react";
import { RouteComponentProps } from "@reach/router";
import { Typography } from "@material-ui/core";
import { useCommonWidths } from "../../../hooks";
import DraftsIcon from "@material-ui/icons/Drafts";
import EmailIcon from "@material-ui/icons/Email";
import { LoginResource } from "../../../resources";
import { MyPagePaper, MyPagePanel, MyPageDataTable } from "../../common";
import { FormattedMessage, defineMessages, injectIntl } from "react-intl";
import MessageView from "./MessageView";
import { useFetcher } from "rest-hooks";
import { MessageModel } from "../../../resources/MessageResource";

interface MessagesProps extends RouteComponentProps {
	intl: any;
	allMessages: MessageModel[];
}

const tableColumns = [
	{
		title: "",
		field: "opened",
		forMobile: true,
		render: (rowdata: MessageModel) =>
			rowdata.opened ? <DraftsIcon /> : <EmailIcon />,
	},
	{
		title: (
			<FormattedMessage
				id="minside.messages.table.title"
				defaultMessage="Tittel"
				description="Title header in messages table"
			/>
		),
		field: "title",
		forMobile: true,
	},
	{
		title: (
			<FormattedMessage
				id="minside.messages.table.title"
				defaultMessage="Kjøretøy"
				description="Vehicle header in messages table"
			/>
		),
		field: "objectCar",
		forMobile: true,
	},
	{
		title: (
			<FormattedMessage
				id="minside.messages.table.message_type"
				defaultMessage="Type"
				description="Message type header in messages table"
			/>
		),
		field: "messageType",
		forMobile: false,
	},
	{
		title: (
			<FormattedMessage
				id="minside.messages.table.recieved_date"
				defaultMessage="Mottatt"
				description="Recieved date header in messages table"
			/>
		),
		field: "receivedDateDisplay",
		forMobile: false,
	},
];

const Messages = (props: MessagesProps) => {
	const isMobile = useCommonWidths().isSmDown;
	const columnsToUse = isMobile
		? tableColumns.filter((c) => c.forMobile === isMobile)
		: tableColumns;

	const { allMessages } = props;

	const [filteredMessages, setFilteredMessages] = useState(allMessages);
	const [searchText, setSearchText] = useState("");
	const [activeMessageIndex, setActiveMessageIndex] = useState(-1);

	const loginStatus = useFetcher(LoginResource.statusShape());

	useEffect(() => {
		loginStatus({});
	}, [loginStatus]);

	return (
		<MyPagePanel>
			<Typography variant="h4" gutterBottom align="center">
				<FormattedMessage
					id="minside.messages.table.header"
					defaultMessage="Dine meldinger"
					description="Header for messages table"
				/>
			</Typography>

			{activeMessageIndex >= 0 ? (
				<MessageView
					filteredMessages={filteredMessages}
					activeMessageIndex={activeMessageIndex}
					setFilteredMessages={setFilteredMessages}
					onReturn={setActiveMessageIndex}
				/>
			) : (
				<MyPagePaper>
					<MyPageDataTable
						columns={columnsToUse}
						data={filteredMessages.map((m: MessageModel) => {
							return {
								...m,
								title: isMobile ? `${m.title}\n${m.receivedDateDisplay}` : m.title,
							};
						})}
						onRowClick={(_, rowData: MessageModel) => {
							setActiveMessageIndex(
								filteredMessages.findIndex((m) => m.id === rowData.id)
							);
						}}
						onSearchChange={(e: string) => {
							setSearchText(e);
							setFilteredMessages(
								allMessages.filter(
									(item) =>
										item.title.toLowerCase().includes(e.toLowerCase()) ||
										item.objectCar.toLowerCase().includes(e.toLowerCase()) ||
										item.messageType.toLowerCase().includes(e.toLowerCase())
								)
							);
						}}
						options={{
							rowStyle: (rowdata: MessageModel) => {
								return rowdata.opened
									? { color: "grey" }
									: { fontWeight: "bold" };
							},
							searchText: searchText,
						}}
						localization={{
							body: {
								emptyDataSourceMessage: props.intl.formatMessage(
									messages.emptyDataSourceMessage
								),
							},
						}}
					/>
				</MyPagePaper>
			)}
		</MyPagePanel>
	);
};
const messages = defineMessages({
	emptyDataSourceMessage: {
		id: "minside.messages.table.header.no_data",
		defaultMessage: "Ingen meldinger å vise",
		description: "No messages in datatable",
	},
});

export default injectIntl(Messages);
